import type { GetCheckoutResponse } from "../services/checkout-service";

export const ACTIVE_CHECKOUT_STORAGE_KEY = "active_org_checkout";

export interface StoredCheckoutState {
  sessionId: string;
  planCode: string;
  action: string;
  currentStep: "payment";
  paymentSubmitted: boolean;
}

export type NormalizedCheckoutStatus =
  | "pending"
  | "processing"
  | "paid"
  | "failed"
  | "cancelled"
  | "expired"
  | "unknown";

export const isValidActiveCheckout = (
  value: unknown,
): value is StoredCheckoutState => {
  if (!value || typeof value !== "object") return false;

  const checkout = value as Partial<StoredCheckoutState>;
  return (
    typeof checkout.sessionId === "string" &&
    checkout.sessionId.trim().length > 0 &&
    typeof checkout.planCode === "string" &&
    checkout.planCode.trim().length > 0 &&
    typeof checkout.action === "string" &&
    checkout.action.trim().length > 0 &&
    checkout.currentStep === "payment" &&
    typeof checkout.paymentSubmitted === "boolean"
  );
};

export const clearActiveCheckout = (): void => {
  try {
    window.sessionStorage.removeItem(ACTIVE_CHECKOUT_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
};

export const readActiveCheckout = (): StoredCheckoutState | null => {
  try {
    const serialized = window.sessionStorage.getItem(
      ACTIVE_CHECKOUT_STORAGE_KEY,
    );
    if (!serialized) return null;

    const parsed: unknown = JSON.parse(serialized);
    if (!isValidActiveCheckout(parsed)) {
      clearActiveCheckout();
      return null;
    }

    return parsed;
  } catch {
    clearActiveCheckout();
    return null;
  }
};

export const saveActiveCheckout = (checkout: StoredCheckoutState): boolean => {
  if (!isValidActiveCheckout(checkout)) return false;

  try {
    window.sessionStorage.setItem(
      ACTIVE_CHECKOUT_STORAGE_KEY,
      JSON.stringify(checkout),
    );
    return true;
  } catch {
    return false;
  }
};

export const isZeroAmount = (amount: string): boolean => {
  const parsed = Number(amount);
  return Number.isFinite(parsed) && parsed === 0;
};

export const buildCheckoutMetadata = (checkout: StoredCheckoutState) => ({
  checkout_session_id: checkout.sessionId,
  plan_code: checkout.planCode,
});

export const normalizeCheckoutStatus = (
  response: GetCheckoutResponse,
): NormalizedCheckoutStatus => {
  const status =
    typeof response?.status === "string"
      ? response.status.trim().toLowerCase()
      : "";

  if (["pending", "created", "initiated"].includes(status)) return "pending";
  if (["processing", "in_progress", "verifying"].includes(status)) {
    return "processing";
  }
  if (["paid", "success", "completed", "succeeded"].includes(status)) {
    return "paid";
  }
  if (["failed", "failure", "declined"].includes(status)) return "failed";
  if (["cancelled", "canceled"].includes(status)) return "cancelled";
  if (status === "expired") return "expired";
  return "unknown";
};
